import React, { useState, useRef, useCallback } from "react";
import "./QuestionCarousel.css";

export interface QuestionItem {
  category: string;
  question: string;
}

interface QuestionCarouselProps {
  questions: QuestionItem[];
  onQuestionClick: (question: string) => void;
}

const QuestionCarousel: React.FC<QuestionCarouselProps> = ({
  questions,
  onQuestionClick,
}) => {
  const [currentIndex, setCurrentIndex] = useState(() =>
    Math.floor(Math.random() * questions.length)
  );
  const carouselRef = useRef<HTMLDivElement>(null);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % questions.length);
  }, [questions.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + questions.length) % questions.length);
  }, [questions.length]);

  const handleCardClick = useCallback((index: number) => {
    if (index === currentIndex) {
      onQuestionClick(questions[currentIndex].question);
    }
  }, [currentIndex, onQuestionClick, questions]);

  const getCardStyle = (index: number) => {
    const diff = index - currentIndex;
    const normalizedDiff =
      diff > questions.length / 2
        ? diff - questions.length
        : diff < -questions.length / 2
        ? diff + questions.length
        : diff;

    let transform = `scale(0.5)`;
    let opacity = 0;
    let zIndex = 0;

    if (normalizedDiff === 0) {
      transform = `scale(1)`;
      opacity = 1;
      zIndex = 10;
    } else if (normalizedDiff === -1) {
      transform = `translateX(-80%) scale(0.8)`;
      opacity = 0.3;
      zIndex = 5;
    } else if (normalizedDiff === 1) {
      transform = `translateX(80%) scale(0.8)`;
      opacity = 0.3;
      zIndex = 5;
    }

    return {
      transform,
      opacity,
      zIndex,
      transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    };
  };

  return (
    <div className="question-carousel-wrapper">
      <div className="carousel-hint">
        Klicke auf eine Frage oder wische zum Durchblättern.
      </div>

      <div className="carousel-container">
        <button
          className="carousel-arrow carousel-arrow-left"
          onClick={goToPrevious}
          aria-label="Vorherige Frage"
        >
          ‹
        </button>

        <div
          className="question-carousel"
          ref={carouselRef}
        >
          {questions.map((item, index) => {
            const style = getCardStyle(index);
            const isActive = index === currentIndex;

            return (
              <div
                key={index}
                className={`question-card ${isActive ? "active" : ""}`}
                style={style}
                onClick={() => handleCardClick(index)}
              >
                <div className="card-border"></div>
                <div className="card-content">
                  <div className="card-category">{item.category}</div>
                  <div className="card-question">{item.question}</div>
                </div>
              </div>
            );
          })}
        </div>

        <button
          className="carousel-arrow carousel-arrow-right"
          onClick={goToNext}
          aria-label="Nächste Frage"
        >
          ›
        </button>
      </div>

      <div className="carousel-indicators">
        {questions.map((_, index) => (
          <div
            key={index}
            className={`indicator ${index === currentIndex ? "active" : ""}`}
            onClick={() => setCurrentIndex(index)}
          />
        ))}
      </div>
    </div>
  );
};

export default QuestionCarousel;
